import { useState, useEffect, useRef } from 'react';
import { Button, Col, Form, ListGroup, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark as solidXmark } from '@fortawesome/pro-solid-svg-icons';
import { faMagnifyingGlass, faCircleXmark as lightXmark } from '@fortawesome/pro-light-svg-icons';
import DropDownSearch from '../../../../components/DropDownSearch';
import SkeletonGrid from '../../../../components/SkeletonLoader/SkeletonGrid';
import TreeSelect from '../../../../components/TreeSelect/index';
import { useActivitySearchContext } from '../../context';
import { useGetActivityCourseLevelsMutation, useGetActivityTopicsByLevelMutation } from '../../../../features/authentication/activitySearchApiSlice';
import HesSpinner from '../../../../components/loadingOverlay/hesSpinner';
import {useGetBasicSearchVocabularyMutation, useUpdateBasicSearchVocabularyMutation} from '../../../../features/authentication/persistApiSlice';

const ActivitySearchFilters = () => {
  const searchBoxRef = useRef();
  const activitySearchContext = useActivitySearchContext();

  const [searchType, setSearchType] = useState('basic');
  const [courseFilterOptions, setCourseFilterOptions] = useState([]);
  const [keyword, setKeyword] = useState();
  const [clearHover, setClearHover] = useState(false);
  const [courseLevelIds, setCourseLevelIds] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(-1);
  const [topicNodes, setTopicNodes] = useState([]);
  const [selectedTopicNodes, setSelectedTopicNodes] = useState([]);
  const [topicNodesCount, setTopicNodeCount] = useState(0);
  const [foreignCourses, setForeignCourses] = useState([]);

  const [getCourseLevels, {isLoading : isCourseLevelsLoading}] = useGetActivityCourseLevelsMutation();
  const [getTopicsByLevel, {isLoading : isTopicsByLevelLoading}] = useGetActivityTopicsByLevelMutation();
  const [getBasicSearchVocabulary] = useGetBasicSearchVocabularyMutation();
  const [updateBasicSearchVocabulary] = useUpdateBasicSearchVocabularyMutation();
  const foreignLanguageCourseId = 105;

  useEffect(() => {
    if (activitySearchContext.primaryCourseFilters.length == 0) return;
    var courses = [...activitySearchContext.primaryCourseFilters];
    courses.pop();
    setCourseFilterOptions(courses);
  }, [activitySearchContext.primaryCourseFilters]);

  useEffect(() => {
    if (courseFilterOptions.length === 0) return;
    
    const mapPersistedLevelsToGradeFilterIds = (levelIds) => {
      return activitySearchContext.gradeFilterOptions
        .filter((gradeFilter) => gradeFilter.gradeId.some((gradeId) => levelIds.includes(gradeId)))
        .map((gradeFilter) => gradeFilter.id);
    };

    const fetchPersistCourseAndLevel = async () => {
      try {
        const response = await getBasicSearchVocabulary().unwrap();

        if(response.data){
          const courseId = Number(response.data.courseId);
          const selectedGradeFilterIds = mapPersistedLevelsToGradeFilterIds(response.data.levelIds);

          if (!Number.isNaN(courseId) && courseId > 0) {
            const selectedCourse = courseFilterOptions.find(
              (course) => course.mainCourse.id === courseId
            );
            if (selectedCourse && selectedGradeFilterIds.length > 0) {
              handleBasicTopLevelCourseSelection(selectedCourse);
              activitySearchContext.setQuestionsTabBasicGradeIds(selectedGradeFilterIds);
              topicTreeHandler('', 'root', false, null, null, selectedCourse.mainCourse.id, selectedGradeFilterIds);
            }
          }
        }
        
      } catch (error) {
        console.error('Error fetching persisted basic search course and level:', error);
      }
    };

    fetchPersistCourseAndLevel();
  }, [courseFilterOptions]);

  const toggleAllCourses = (filterList) => {
    const hasUnchecked = filterList.some(item => !item?.isChecked);
    return filterList.map((item) => ({
      ...item,
      isChecked: hasUnchecked,
    }));
  };

  const toggleSingleCourse = (filterList, key, selected) => {
    return filterList.map((item) =>
      item.id == key ? { ...item, isChecked: !selected.isChecked } : item
    );
  };

  const getSelectedCourseIds = (tempValue, filterList, key) => {
    const baseSelected = activitySearchContext.questionsTabAdvSelectedCourseIds.filter(
      s => !filterList.some(l => l.id == s)
    );

    const allChecked = tempValue.filter(v => v.isChecked).length === filterList.length;
    if (allChecked) {
      return baseSelected.concat(filterList.slice(1).map(f => f.id));
    }

    if (key !== filterList[0].id) {
      return baseSelected.concat(
        tempValue.filter(v => v.isChecked && v.id !== filterList[0].id).map(v => v.id)
      );
    }
    return baseSelected;
  };

  const handleAdvancedCourseSelectionChange = (e, filterList, setFilterList) => {
    const { key } = e;
    const isSelectAll = key === '000';
    const selected = !isSelectAll ? filterList.find((v) => v.id == key) : null;
    
    const tempValue = isSelectAll 
      ? toggleAllCourses(filterList)
      : toggleSingleCourse(filterList, key, selected);

    const tempSelectedCourses = getSelectedCourseIds(tempValue, filterList, key);
    
    activitySearchContext.setQuestionsTabAdvSelectedCourseIds(tempSelectedCourses);
    setFilterList(tempValue);
  };

  const handleBasicGradeLevelSelection = (gradeFilter) => {
    var isSelected = !activitySearchContext.questionsTabBasicGradeIds.includes(gradeFilter.id);

    var selectedGradeIds = [];
    if (isSelected) {
      selectedGradeIds = [...activitySearchContext.questionsTabBasicGradeIds, gradeFilter.id];
    } else {
      selectedGradeIds = activitySearchContext.questionsTabBasicGradeIds.filter(g => g != gradeFilter.id);
    }
    activitySearchContext.setQuestionsTabBasicGradeIds(selectedGradeIds);
    setTopicNodes([]);
    setSelectedTopicNodes([]);
    setTopicNodeCount(0);
  };

  const handleForeignLanguageSelction = (parent) => {  
    const updatedCourses = foreignCourses.map(course =>
      course.parent == parent ? { ...course, isSelected: !course.isSelected } : course
    );
    setForeignCourses(updatedCourses);
  };

  const handleBasicTopLevelCourseSelection = (item) => {
    if (item.mainCourse.id == activitySearchContext.questionsTabBasicCourse?.mainCourse?.id) return;
    let allCourses = [];
    if (item.mainCourse.id == foreignLanguageCourseId) {
      allCourses = item.subCourses.map(item => item.id);
      let courses = [];
      item.subCourses.slice(1).map((item) => (
        courses.push({
          id: item.id,
          name: item.name,
          parent: item.name.includes('Spanish') ? 'Spanish' : 'French',
          isSelected: false
        })
      ));
      const sortedArray = courses.sort((a, b) => a.id - b.id);
      setForeignCourses(sortedArray);
    }
    else {
      allCourses = [item.mainCourse.id];
    }
    getCourseLevelsAsync(allCourses);
    setSelectedCourseId(item.mainCourse.id);
    activitySearchContext.setQuestionsTabBasicCourse(item);
    setCourseLevelIds([]);
    activitySearchContext.setQuestionsTabBasicGradeIds([]);
    setTopicNodes([]);
    setSelectedTopicNodes([]);
    setTopicNodeCount(0);
  };

  const getCourseLevelsAsync = async (courseIds) => {
    const params = new URLSearchParams();
    courseIds.forEach(item => params.append('ids', item));
    var response = await getCourseLevels(params.toString()).unwrap();
    const levelIds = response.map(item => item.id);
    setCourseLevelIds(levelIds);
  };

  const SearchVocabularies = () => {
    const searchRequest = {
      pageNumber: 1,
      pageSize: 25,
      ...(searchType === 'basic' ? getBasicSearchRequest() : getAdvancedSearchRequest())
    };

    activitySearchContext.setShowResults(true);
    activitySearchContext.setApiParams(searchRequest);
    activitySearchContext.setApiIdentifier('vocabularySearch');
  };
  
  const getBasicSearchRequest = () => {
    const courseIds = selectedCourseId === foreignLanguageCourseId
      ? [...foreignCourses.filter(item => item.isSelected).map(item => item.id), foreignLanguageCourseId]
      : activitySearchContext.questionsTabBasicCourse.subCourses.map(c => c.id);

    const levels = activitySearchContext.gradeFilterOptions
      .filter(g => activitySearchContext.questionsTabBasicGradeIds.includes(g.id))
      .map(g => g.gradeId)
      .flat();

    return {
      courseIds,
      levels,
      topics: selectedTopicNodes.flat()
    };
  };

  const getAdvancedSearchRequest = () => ({
    courseIds: activitySearchContext.questionsTabAdvSelectedCourseIds,
    keyword
  });

  const handleClearSearch = () => {
    searchBoxRef.current.value = '';
    searchBoxRef.current.focus();
    setKeyword('');
  };

  const topicTreeHandler = async (rootName, nodeValue, isChildAvailable, childrens, index, persistedCourseId, persistedGradeIds) => {
    let response = [];
    if (isChildAvailable)
      response = childrens;
    else {
      const selectedGradeIds = Array.isArray(persistedGradeIds)
        ? persistedGradeIds
        : activitySearchContext.questionsTabBasicGradeIds;
      const levels = activitySearchContext.gradeFilterOptions
        .filter(g => selectedGradeIds.includes(g.id))
        .map(g => g.gradeId)
        .flat();
      const courseId = Number.isFinite(Number(persistedCourseId)) ? Number(persistedCourseId) : selectedCourseId;
      const option = {
        courseId,
        levelIds: levels
      };
      await updateBasicSearchVocabulary(option).unwrap();
      response = await getTopicsByLevel(option).unwrap();
      setTopicNodes(response);
    }
    setTreeNodes(response, nodeValue, rootName, topicNodes, setTopicNodes, setTopicNodeCount, index);
  };

  const setTreeNodes = async (response, nodeValue, rootName, nodes, setNodes, setNodeCount, treeIndex) => {
    try {
      if (nodeValue == 'root') {
        setNodes(response);
        setNodeCount(response.length);
      } else {
        const newIds = response.map((newItem) => newItem.id);
        if (
          !nodes[treeIndex]
            .find((i) => i.name == rootName)
            .childrens.some((item2) => item2.id == newIds[0])
        ) {
          var selectedTree = nodes[treeIndex];
          const updatedTree = selectedTree.map((item) => {
            if (item.name === rootName) {
              const updatedChildrens = item.childrens.map((child) => {
                if (child.id == nodeValue) {
                  return { ...child, children: newIds };
                }
                return child;
              });
              return { ...item, childrens: [...updatedChildrens, ...response] };
            }
            return item;
          });
          let tempTree = [...nodes];
          tempTree[treeIndex] = updatedTree;
          setNodes(tempTree);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const isGradeDisabled = (grade) =>
    !grade.gradeId.some((id) => courseLevelIds.includes(id));

  const getGroupedCourses = (courses) => {
    return courses.reduce((acc, course) => {
      if (!acc[course.parent]) {
        acc[course.parent] = [];
      }
      acc[course.parent].push(course);
      return acc;
    }, {});
  };

  return (
    <div className='d-flex flex-column castleQuestionsSearchBox'>

      {activitySearchContext.courseLoading && <div className='d-flex flex-row w-100 justify-content-center align-items-center'>
        <HesSpinner width='2%' />
        <span style={{ fontSize: '2em' }} className='ms-2'>Getting your courses...</span>
      </div>}

      {activitySearchContext.primaryCourseFilters.length > 0 && <div className='castleQuestionsSearchTypeList mb-3'>
        <span className={` px-3 py-2 ${searchType == 'basic' ? 'active' : ''} border-radius-left-7px`} onClick={() => {setSearchType('basic'); activitySearchContext.setResetQuestions(true);}}>Basic Search</span>
        <span className={`px-3 py-2 ${searchType == 'advanced' ? 'active' : ''} border-radius-right-7px`} onClick={() => {setSearchType('advanced');activitySearchContext.setResetQuestions(true);}}>Advanced Search</span>
      </div>}

      {searchType === 'basic' ?
        <div className='d-flex flex-column'>
          {activitySearchContext.primaryCourseFilters.length > 0 &&
            <div className='mb-3'>
              <div className='d-flex flex-column w-100 mb-3 mt-3'>
                <span className='fw-500'>Select Course</span>
                <div className='d-flex flex-row justify-content-start'>
                  {courseFilterOptions.map((item, index) => {
                    return (
                      <Button key={`courseFilter${index}`} variant='castleGray' className={`w-25 filterButton ${item.mainCourse.id === activitySearchContext.questionsTabBasicCourse?.mainCourse?.id ? 'active' : ''}`}
                        onClick={() => handleBasicTopLevelCourseSelection(item)}>{item.mainCourse.name}</Button>
                    );
                  })}
                </div>
              </div>
              {selectedCourseId >= 0 &&
                <>
                  {isCourseLevelsLoading ?
                    (<SkeletonGrid
                      rowCount={1}
                      colCount={12}
                      className='p-3'
                    />) : (
                      <div className='d-flex flex-column w-100'>
                        <span className='fw-500'>Select Grade</span>
                        <div className='d-flex flex-row justify-content-between'>
                          {activitySearchContext.gradeFilterOptions.map((item, index) => {
                            return (
                              <Button key={`gradeFilter${index}`} variant='castleGray' className={`filterButton ${activitySearchContext.questionsTabBasicGradeIds.includes(item.id) ? 'active' : ''} ${isGradeDisabled(item) ? 'text-grey cursor-not-allowed' : ''}`}
                                onClick={() => handleBasicGradeLevelSelection(item)} disabled={isGradeDisabled(item)}>{item.name}</Button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                </>
              }
            </div>
          }
          {(selectedCourseId === foreignLanguageCourseId && activitySearchContext.questionsTabBasicGradeIds.length > 0) ? (
            <div>
              <span className="fw-500 mb-3">Select Topic</span>
              <Row className="mt-4">
                {Object.entries(getGroupedCourses(foreignCourses)).map(([parent, courseList]) => (
                  <Col className="col-2" key={parent}>
                    <Button
                      variant="castleGray"
                      className={`filterButton ${courseList.filter(item => item.isSelected).length > 0 ? 'active' : ''}`}
                      onClick={() => handleForeignLanguageSelction(parent)}
                    >
                      {parent}
                    </Button>
                  </Col>
                ))}
              </Row>
            </div>            
          ) : (
            <div className="mt-4">
              {isTopicsByLevelLoading ? (
                <SkeletonGrid rowCount={1} colCount={1} className="p-2" />
              ) : (
                <>
                  {topicNodes.length > 0 &&
                <>
                  <span className='fw-500 mb-3'>Select Topic</span>
                  <Row>
                    {topicNodes.map((node,index)=>  (
                      <Col className='col-4 mt-2' key={index}>
                        <TreeSelect
                          key={index}
                          nodes={node}
                          treeNodeHandler={topicTreeHandler}
                          parentNodesCount={topicNodesCount}
                          selectedChildIds={selectedTopicNodes}
                          setSelectedChildIds={setSelectedTopicNodes}
                          treeIndex={index}
                        />
                      </Col>
                    ))} 
                  </Row>
                </>}
                </>
              )
              }
            </div>
          )
          }
          {activitySearchContext.primaryCourseFilters.length > 0 && activitySearchContext.questionsTabBasicGradeIds != null && activitySearchContext.questionsTabBasicCourse != null &&
            <div className='d-flex flex-row justify-content-center align-items-center gap-4 mt-4'>
              {selectedCourseId != foreignLanguageCourseId && topicNodes.length > 0 ? (
                <Button variant={'castleRed'}
                  className='w-20'
                  onClick={() => SearchVocabularies()} 
                  disabled={activitySearchContext.isSearching || activitySearchContext.questionsTabBasicGradeIds?.length == 0 || activitySearchContext.questionsTabBasicCourse == null || selectedTopicNodes.length == 0}>
                  Show me Vocabularies
                </Button>
              ) : selectedCourseId != foreignLanguageCourseId ? (
                <Button variant={'castleWhite'}
                  className='w-20'
                  onClick={() => topicTreeHandler('', 'root')} 
                  disabled={activitySearchContext.isSearching || activitySearchContext.questionsTabBasicGradeIds?.length == 0 || activitySearchContext.questionsTabBasicCourse == null}>
                  Search
                </Button>
              ) : (
                <Button variant={'castleRed'}
                  className='w-20'
                  onClick={() => SearchVocabularies()} 
                  disabled={activitySearchContext.isSearching || activitySearchContext.questionsTabBasicGradeIds?.length == 0 || activitySearchContext.questionsTabBasicCourse == null || foreignCourses.filter(item=> item.isSelected).length == 0}>
                  Show me Vocabularies
                </Button>
              )}
            </div>
          }
        </div>
        :
        <div className='d-flex flex-column'>
          <span className='mb-3 fw-500'>Choose Courses</span>
          <div className='d-flex flex-row gap-2 mb-2 advanced-course-row'>
            {[
              { label: 'Math', courses: activitySearchContext.questionsTabAdvMathCourses, setCourses: activitySearchContext.setQuestionsTabAdvMathCourses, id: 'mathCourseFilter', excludeId: 101, selectAllText: 'All Math Courses' },
              { label: 'Social Studies', courses: activitySearchContext.questionsTabAdvSocialStudiesCourses, setCourses: activitySearchContext.setQuestionsTabAdvSocialStudiesCourses, id: 'socialStudiesCourseFilter', excludeId: 103, selectAllText: 'All Social Studies Courses' },
              { label: 'Science', courses: activitySearchContext.questionsTabAdvScienceCourses, setCourses: activitySearchContext.setQuestionsTabAdvScienceCourses, id: 'scienceCourseFilter', excludeId: 102, selectAllText: 'All Science Courses' },
              { label: 'ELA', courses: activitySearchContext.questionsTabAdvELACourses, setCourses: activitySearchContext.setQuestionsTabAdvELACourses, id: 'elaFilterList', excludeId: 104, selectAllText: 'All ELA Courses' },
              { label: 'Foreign Language', courses: activitySearchContext.questionsTabAdvForeignLanguageCourses, setCourses: activitySearchContext.setQuestionsTabAdvForeignLanguageCourses, id: 'foreignLangFilterList', excludeId: foreignLanguageCourseId, selectAllText: 'All Foreign Language Courses' },
              { label: 'Other', courses: activitySearchContext.questionsTabAdvOtherCourses, setCourses: activitySearchContext.setQuestionsTabAdvOtherCourses, id: 'otherCourseFilter', excludeId: 0, selectAllText: 'All Other Courses' },
            ].map((courseFilter) => (
              activitySearchContext[`questionsTabAdv${courseFilter.label.replace(/\s+/g, '')}Courses`]?.length > 0 && (
                <div key={courseFilter.id} className='d-flex flex-column advanced-course-col'>
                  <span>{courseFilter.label}</span>
                  <DropDownSearch
                    value={courseFilter.courses.filter(m => m.id != courseFilter.excludeId)}
                    setValue={courseFilter.setCourses}
                    dropdownId={courseFilter.id}
                    placeholder='Select Course'
                    handleChange={(e) => handleAdvancedCourseSelectionChange(e, courseFilter.courses, courseFilter.setCourses)}
                    selectedId={courseFilter.courses.filter(m => m.isChecked && m.id != courseFilter.excludeId).map(m => m.id)}
                    description={true}
                    selectAllText={courseFilter.selectAllText}
                  />
                </div>
              )
            ))}
          </div>

          <div className='d-flex flex-row gap-2 align-items-center d-relative'>
            <ListGroup.Item className='mx-1 w-100'>
              <Form.Group id="standardSearchGroup">
                <div>
                  <Form.Control type='text' className='p-inline-35' placeholder='Keyword' ref={searchBoxRef} defaultValue={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onKeyUp={(e) => { if (activitySearchContext.questionsTabAdvSelectedCourseIds.length > 0 && e.key == 'Enter') SearchVocabularies(); }} />
                  <FontAwesomeIcon className='searchIcon' icon={faMagnifyingGlass} />
                  <FontAwesomeIcon className={`clearSearchIcon ${keyword?.length > 0 ? '' : 'd-none'}`}
                    icon={clearHover ? solidXmark : lightXmark}
                    onMouseEnter={() => setClearHover(true)}
                    onMouseLeave={() => setClearHover(false)}
                    onClick={() => handleClearSearch()}
                  />
                </div>
              </Form.Group>
            </ListGroup.Item>
            <Button variant='castleWhite btn-width' disabled={activitySearchContext.isSearching || activitySearchContext.questionsTabAdvSelectedCourseIds.length <= 0} onClick={() => SearchVocabularies()}>Search</Button>
          </div>
        </div>
      }
    </div>
  );
};

export default ActivitySearchFilters;